import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import Footer from '../Footer';
import { useLaunchQuiz } from '../../lib/fiQuiz';
import * as events from '../../lib/analytics/events';
import { PRODUCT_ORDER, PRODUCTS, ProductInfo, SECTIONS, TooltipKey } from './compareData';
import { GRID_TEMPLATE, INK, MOBILE, NEUTRAL, SURFACE } from './constants';
import { SectionIcon } from './CompareIcons';
import { useCompareColumns } from './hooks/useCompareColumns';
import { useStickyHeaderOffset } from './hooks/useStickyHeaderOffset';
import { AttributeCell } from './components/AttributeCell';
import { CollarSelector } from './components/CollarSelector';
import { ProductCard } from './components/ProductCard';
import { QuizCta } from './components/QuizCta';
import { ReasonsBlock } from './components/ReasonsBlock';
import { TooltipModal } from './components/TooltipModal';

/**
 * Loop-inspired compare page (CompareV2), styled to the fitracking tiled matrix —
 * white cells on a hairline grid. Desktop shows three collars, mobile two; each
 * column has its own selector and picking a shown collar swaps the two. Relies on
 * the global AppBar and shared Footer.
 */
const CompareV2: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const launchQuiz = useLaunchQuiz();
  const headRef = useRef<HTMLDivElement>(null);

  const { columns, selectColumn } = useCompareColumns();
  const stuck = useStickyHeaderOffset(headRef);
  const [openTooltip, setOpenTooltip] = useState<TooltipKey | null>(null);
  // Tap-to-change collar color (S3 is the only product with swatches).
  const [s3Image, setS3Image] = useState<string>(PRODUCTS.s3.image);

  useEffect(() => {
    events.comparePageViewed();
  }, []);

  const handleShop = (product: ProductInfo) => {
    events.comparePageBuyItClicked(product.trackName);
    history.push(product.route);
  };

  const handleQuiz = () => {
    events.quizQuestionPage.quizButtonTapped();
    launchQuiz();
  };

  const options = PRODUCT_ORDER.map((key) => ({ value: key, label: t(PRODUCTS[key].dropdownLabelKey) }));

  return (
    <>
      <Box w="100%" bg={NEUTRAL}>
        <Box maw={1160} mx="auto" sx={{ padding: '40px 40px 96px', [MOBILE]: { padding: '24px 20px 48px' } }}>
          <Box ta="center" sx={{ marginBottom: 28, [MOBILE]: { marginBottom: 24 } }}>
            <Box component="h1" m={0} fw={700} lts="-0.02em" sx={{ fontSize: 'clamp(40px, 9vw, 80px)', lineHeight: 1 }}>
              {t('compare_v2_hero_title')}
            </Box>
          </Box>

          {/* Sticky selector + Shop cluster */}
          <Box
            ref={headRef}
            sx={{
              ...GRID_TEMPLATE,
              position: 'sticky',
              // Pinned beneath the app's sticky header (height measured at runtime).
              top: 'var(--compare-sticky-top, 96px)',
              zIndex: 50,
              background: NEUTRAL,
              padding: '14px 0 16px',
              transition: 'box-shadow 0.2s ease',
              boxShadow: stuck ? '0 8px 10px -8px rgba(0, 0, 0, 0.16)' : 'none',
            }}
          >
            {columns.map((key, index) => (
              <CollarSelector
                key={`head-${key}-${index}`}
                index={index}
                product={PRODUCTS[key]}
                options={options}
                onSelect={selectColumn}
                onShop={handleShop}
              />
            ))}
          </Box>

          {/* Comparison matrix — white cells on a 2px hairline grid */}
          <Box
            sx={{
              ...GRID_TEMPLATE,
              rowGap: 2,
              background: NEUTRAL,
              borderRadius: 8,
              overflow: 'hidden',
              '& > *': { background: SURFACE, minWidth: 0 },
            }}
          >
            {columns.map((key) => {
              const product = PRODUCTS[key];
              return (
                <ProductCard
                  key={`card-${key}`}
                  product={product}
                  image={product.swatches ? s3Image : product.image}
                  onSelectColor={product.swatches ? setS3Image : undefined}
                />
              );
            })}
            {columns.map((key) => (
              <ReasonsBlock key={`reasons-${key}`} product={PRODUCTS[key]} />
            ))}
            {SECTIONS.map((section) => (
              <React.Fragment key={section.titleKey}>
                <Box sx={{ gridColumn: '1 / -1', padding: '22px 20px 14px', [MOBILE]: { padding: '16px 12px 8px' } }}>
                  <Box
                    fw={600}
                    c={INK}
                    lts="-0.4px"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 10,
                      fontSize: 20,
                      '& svg': { width: 24, height: 24, flex: 'none' },
                      [MOBILE]: { fontSize: 14, gap: 7, '& svg': { width: 18, height: 18 } },
                    }}
                  >
                    <SectionIcon name={section.icon} />
                    {t(section.titleKey)}
                  </Box>
                </Box>
                {section.rows.map((row) => (
                  <React.Fragment key={`${section.titleKey}-${row.attrKey}`}>
                    {columns.map((key) => (
                      <AttributeCell
                        key={`${row.attrKey}-${key}`}
                        row={row}
                        product={PRODUCTS[key]}
                        onOpenTooltip={setOpenTooltip}
                      />
                    ))}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
          </Box>

          <QuizCta onLaunch={handleQuiz} />
        </Box>
      </Box>

      <Footer />

      <TooltipModal tooltipKey={openTooltip} onClose={() => setOpenTooltip(null)} />
    </>
  );
};

export default CompareV2;
